import { useCallback } from 'react';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { usePlatform } from './usePlatform';

export const useHaptics = () => {
  const { isNative, isIOS } = usePlatform();

  // Light impact - for tab switches, minor interactions
  const lightImpact = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Medium impact - for button presses
  const mediumImpact = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Heavy impact - for important actions
  const heavyImpact = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Selection change - for picker/selection changes
  const selectionChanged = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.selectionChanged();
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Success notification
  const successNotification = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Warning notification
  const warningNotification = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  // Error notification
  const errorNotification = useCallback(async () => {
    if (!isNative || !isIOS) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  }, [isNative, isIOS]);

  return {
    lightImpact,
    mediumImpact,
    heavyImpact,
    selectionChanged,
    successNotification,
    warningNotification,
    errorNotification,
  };
};

// Static helper for triggering haptics at app startup
export const triggerStartupHaptic = async () => {
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch (error) {
    console.warn('Haptics not available:', error);
  }
};
